import * as XLSX from 'xlsx';

const toText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(toText).filter(Boolean).join(', ');
  }
  return JSON.stringify(value, null, 2);
};

const safeSheetName = (name) => {
  if (name.length <= 31) {
    return name;
  }
  return `${name.slice(0, 28)}…`;
};

const appendSheet = (workbook, name, rows, columnWidths = []) => {
  const sheetRows = rows && rows.length ? rows : [['Aucune donnée disponible']];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
  if (columnWidths.length > 0) {
    worksheet['!cols'] = columnWidths.map((wch) => ({ wch }));
  }
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName(name));
};

const buildCriteriaRows = (items = {}, metadata = {}, locationDetails = {}) => {
  const rows = [
    ['Critère', 'Statut', 'Motif', 'Description', 'Suggestion', 'Emplacement', 'Contexte']
  ];

  if (!items || Object.keys(items).length === 0) {
    rows.push(['Aucun critère disponible']);
    return rows;
  }

  Object.entries(items).forEach(([key, value]) => {
    const title = metadata?.titles?.[key] || key;
    const statusOk = value === true;
    const statusLabel = statusOk ? 'Conforme' : 'À corriger';
    const motif = typeof value === 'string' ? value : '';
    const description = metadata?.descriptions?.[key] ? toText(metadata.descriptions[key]) : '';

    const details = locationDetails?.[key] || {};
    const suggestion = toText(details.suggestion);
    const location = details.location && details.location !== 'Critère non migré'
      ? toText(details.location)
      : '';
    const context = toText(details.context || details.foundText);

    rows.push([
      title,
      statusLabel,
      motif,
      description,
      suggestion,
      location,
      context,
    ]);
  });

  return rows;
};

const buildSemanticRows = (semantiqueGlobale) => {
  const rows = [];
  if (!semantiqueGlobale || typeof semantiqueGlobale !== 'object') {
    rows.push(['Analyse sémantique indisponible']);
    return rows;
  }

  const profil = semantiqueGlobale.profil_lecture_detecte;
  rows.push(['Profil de lecture détecté']);
  if (profil) {
    rows.push(['Nom', toText(profil.nom)]);
    rows.push(['Niveau', profil.niveau !== undefined ? profil.niveau : '']);
  } else {
    rows.push(['Nom', 'Non détecté']);
  }
  rows.push([]);

  const criteres = semantiqueGlobale.criteres;
  const iterableCriteres = Array.isArray(criteres)
    ? criteres
    : (criteres && Object.values(criteres));

  if (iterableCriteres && iterableCriteres.length > 0) {
    rows.push(['Critère', 'Score', 'Catégorie', 'Explication spécifique', 'Explication générique']);
    iterableCriteres.forEach((critere) => {
      if (!critere) {
        return;
      }
      const donnees = critere.donnees || {};
      const scoreValue = typeof donnees.score === 'number'
        ? Math.round(donnees.score * 100) / 100
        : '';
      rows.push([
        critere.nom || critere.key || 'Critère',
        scoreValue,
        toText(donnees.seuil_categorie),
        toText(donnees.explication_specifique),
        toText(donnees.explication_generique),
      ]);
    });
    rows.push([]);
  }

  const thematiques = semantiqueGlobale.thematiques;
  const thematiquesData = thematiques?.donnees;
  if (thematiquesData && Object.keys(thematiquesData).length > 0) {
    rows.push(['Thématique', 'Score', 'Explication', 'Termes détectés']);
    Object.entries(thematiquesData).forEach(([theme, details]) => {
      const termes = details?.details_calcul?.termes_matches;
      rows.push([
        theme,
        typeof details?.score === 'number' ? `${Math.round(details.score * 100)}%` : '',
        toText(details?.explication_specifique || details?.explication_generique),
        Array.isArray(termes) ? termes.join(', ') : '',
      ]);
    });
  }

  return rows;
};

const collectMonths = (matomoMetrics = {}) => {
  const months = [];
  Object.values(matomoMetrics).forEach((values) => {
    if (!values || typeof values !== 'object') {
      return;
    }
    Object.keys(values).forEach((month) => {
      if (!months.includes(month)) {
        months.push(month);
      }
    });
  });
  return months;
};

const buildMatomoRows = (matomoMetrics) => {
  if (!matomoMetrics || Object.keys(matomoMetrics).length === 0) {
    return [['Aucun indicateur Matomo disponible']];
  }

  const months = collectMonths(matomoMetrics);
  if (months.length === 0) {
    return [['Aucun indicateur Matomo disponible']];
  }

  const rows = [['Métrique', ...months]];
  Object.entries(matomoMetrics).forEach(([metric, values]) => {
    const row = [metric];
    months.forEach((month) => {
      const value = values?.[month];
      if (typeof value === 'number') {
        row.push(value);
      } else if (typeof value === 'string') {
        row.push(value);
      } else {
        row.push('—');
      }
    });
    rows.push(row);
  });

  return rows;
};

const buildAdditionalChecksRows = (data) => {
  const rows = [['Vérification', 'Statut']];
  if (!data) {
    rows.push(['Aucune donnée', 'Indisponible']);
    return rows;
  }

  const additionalKeys = [
    'url',
    'title',
    'metaDescription',
    'HnEmpty',
    'h1',
    'intro',
    'imageAlt',
    'h2',
    'p',
    'textBetweenTitles',
    'Sommaire',
    'bold-p',
    'faq',
    'FilDArriane',
    'copyWord',
    'inclusive',
    'unica',
  ];

  additionalKeys.forEach((key) => {
    if (!(key in data)) {
      return;
    }
    const statusOk = data[key] === true;
    const label = key.replace(/([A-Z])/g, ' $1').replace(/-/g, ' ').trim();
    rows.push([label, statusOk ? 'OK' : 'À vérifier']);
  });

  return rows;
};

export const buildAnalysisWorkbook = ({ data, url, keyword, criteriaMeta }) => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const workbook = XLSX.utils.book_new();

  const syntheseRows = [
    ['Information', 'Valeur', 'Total / Détail'],
    ['Date', new Date().toLocaleString('fr-FR'), ''],
    ['URL analysée', url || '—', ''],
    ['Requête clé', keyword || '—', ''],
    ['Nombre de mots significatifs', data.nbrsmot ?? '—', ''],
    [],
    ['Score', 'Valeur', 'Total possible'],
    ['Score global', data.score ?? '—', 100],
    ['Critères obligatoires validés', data.scoreMandatory ?? '—', criteriaMeta?.mandatory?.total ?? '—'],
    ['Critères complémentaires validés', data.scoreOptional ?? '—', criteriaMeta?.optional?.total ?? '—'],
    ['Critères bonus validés', data.scoreBonus ?? '—', criteriaMeta?.bonus?.total ?? '—'],
  ];
  appendSheet(workbook, 'Synthèse', syntheseRows, [36, 50, 22]);

  appendSheet(
    workbook,
    'Critères - Obligatoires',
    buildCriteriaRows(data.mandatory, criteriaMeta?.mandatory, data.locationDetails),
    [32, 14, 32, 40, 40, 32, 40]
  );
  appendSheet(
    workbook,
    'Critères - Complémentaires',
    buildCriteriaRows(data.optional, criteriaMeta?.optional, data.locationDetails),
    [32, 14, 32, 40, 40, 32, 40]
  );
  appendSheet(
    workbook,
    'Critères - Bonus',
    buildCriteriaRows(data.bonus, criteriaMeta?.bonus, data.locationDetails),
    [32, 14, 32, 40, 40, 32, 40]
  );

  appendSheet(
    workbook,
    'Analyse sémantique',
    buildSemanticRows(data.semantiqueGlobale),
    [28, 16, 18, 50, 50]
  );

  appendSheet(
    workbook,
    'Matomo',
    buildMatomoRows(data.matomoMetrics),
    [30, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20]
  );

  appendSheet(
    workbook,
    'Vérifications additionnelles',
    buildAdditionalChecksRows(data),
    [40, 18]
  );

  return workbook;
};

export const exportAnalysisReportToExcel = ({ data, url, keyword, criteriaMeta, filename }) => {
  const workbook = buildAnalysisWorkbook({ data, url, keyword, criteriaMeta });
  if (!workbook) {
    return;
  }
  const finalName = filename || 'rapport-seo.xlsx';
  XLSX.writeFile(workbook, finalName, { compression: true });
};

export default buildAnalysisWorkbook;
